import type {Account, ValueOf} from '@myparcel-frontend/types';
import {useSdk, Status} from '@myparcel-frontend/sdk';
import {get} from '@vueuse/core';
import {storeToRefs} from 'pinia';
import {computed, readonly, ref} from 'vue';
import useAcl from '../useAcl';
import {useConfigStore} from '@/store/config';

export default function useAccount() {
  const configStore = useConfigStore();
  const refs = storeToRefs(configStore);
  const {sdk} = useSdk();
  const {acl} = useAcl();

  const status = ref<ValueOf<typeof Status>>(Status.IDLE);

  const fetchCurrentAccount = async () => {
    const accounts = await sdk.getAccount({
      path: {id: acl.value.account_id},
    });

    configStore.setAccount(accounts[0]);
  };

  const getAccount = async () => {
    if (!configStore.account || !Object.keys(configStore.account).length) {
      await fetchCurrentAccount();
    }

    return configStore.account;
  };

  const getAccountId = async () => {
    const account = await getAccount();

    return computed(() => get(account).id);
  };

  // returns options for the shop select form field
  const getShopOptions = () => {
    if (!configStore.account) {
      throw new Error('Account not fetched: cannot get shop options');
    }

    return configStore.account.shops.map((shop) => ({
      label: shop.name,
      value: shop.id,
    }));
  };

  // TODO: remove the fetchCurrentAccount call when the putAccount returns a 200 with value instead of 204 no content.
  // https://myparcelnl.atlassian.net/browse/FE-1048
  const updateAccount = async (accountData: Partial<Account>): Promise<Account> => {
    status.value = Status.PENDING;

    try {
      await sdk.putAccount({body: [accountData]});
      await fetchCurrentAccount();
    } catch (error) {
      status.value = Status.REJECTED;
      throw error;
    }

    status.value = Status.RESOLVED;
    return configStore.account;
  };

  return {
    getAccount,
    getAccountId,
    getShopOptions,
    updateAccount,
    status: readonly(status),
    account: refs.account,
    shops: refs.shops,
    activeShops: refs.activeShops,
    currentUser: refs.currentUser,
    accountSettings: refs.accountSettings,
    hasOrderMode: refs.hasOrderMode,
  };
}
